import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { NotificationContext } from '../../../../providers/notification';

interface ComponentProps {
    handleClose: () => void;
}

const CreateCountry: React.FC<ComponentProps> = ({ handleClose }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const postCountry = (data: FormData) => {
        return apiCallFormData().post('/Country/Create', data);
    };

    const postCountryMutation = useMutation(['post-country'], postCountry, {retry: 0});

    const postCountryHandler = async (values: FormikValues, resetForm: () => void) => {
        const formData = new FormData();
        formData.append('CountryName', values?.name);

        try {
            await postCountryMutation.mutateAsync(formData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Country successfully added',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-countries']);
                    resetForm();
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: ''
        },
        onSubmit: (values, {resetForm}) => {
            postCountryHandler(values, resetForm);
        },
        validationSchema,
    });

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Country Name'
                        placeholder='Name'
                    />
                    {/* <Field 
                        name='status'
                        type='text'
                        as={Select}
                        label='Status'
                    /> */}
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postCountryMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postCountryMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateCountry;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

